import { useCallback, useRef } from 'react';

export type ChimeKind = 'match' | 'win' | 'miss';

export function useChime() {
  const ctxRef = useRef<AudioContext | null>(null);

  const getCtx = useCallback(() => {
    if (!ctxRef.current && typeof window !== 'undefined' && 'AudioContext' in window) {
      try {
        ctxRef.current = new AudioContext();
      } catch {
        return null;
      }
    }
    return ctxRef.current;
  }, []);

  const play = useCallback(
    (kind: ChimeKind) => {
      const ctx = getCtx();
      if (!ctx) return;
      if (ctx.state === 'suspended') void ctx.resume();

      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      const freq = kind === 'win' ? 660 : kind === 'match' ? 880 : 220;
      const decay = kind === 'win' ? 1.4 : 0.4;
      const peak = kind === 'win' ? 0.15 : 0.08;

      osc.frequency.setValueAtTime(freq, now);
      if (kind === 'win') {
        osc.frequency.exponentialRampToValueAtTime(freq * 1.5, now + 0.15);
      }
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(peak, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + decay);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(now);
      osc.stop(now + decay + 0.1);
    },
    [getCtx]
  );

  return { play };
}
